import React from 'react'

interface CanvasApplyTasksIconProps {
  size?: number
  /**
   * Strichstärke im 24er-Koordinatenraum. Default 2 für die **Options-Leiste**:
   * Dort wird das Icon bei 16px gerendert (effektiv 1,33px) und steht neben
   * Lucide-Icons mit Strich 2 — mit dem Figma-Wert 1,5 wirkte es daneben dünn.
   *
   * Die **Werkzeug-Pille** rendert bei 24px und hat solche Nachbarn nicht;
   * dort gilt der Figma-Wert **1,5** (`Icon / FileText` 4916:265176), sonst
   * trägt der Strich bei voller Größe 2px und wird sichtbar zu fett.
   */
  strokeWidth?: number
  className?: string
}

/**
 * "Aufgaben übernehmen"-Icon der Projekt-Canvas-Toolbar (Figma: `Icon / FileText`,
 * Node `4916:265176` in `btn Canvas Toolbar Apply Tasks` 4916:265197).
 * Ein Dokument, aus dem ein Pfeil nach links herauszeigt. Im
 * 24er-Koordinatenraum gezeichnet; die Strichstärke kommt von außen, weil die
 * beiden Einsatzorte verschieden groß rendern — siehe `strokeWidth`.
 *
 * Monochrom über `currentColor` — im Gegensatz zu den beiden farbigen
 * Werkzeug-Icons (`CanvasNoteIcon` gelb, `CanvasMemoIcon` cyan), deren
 * Farbe den Notiz-Typ kennzeichnet. Hier gibt es keinen Typ, also schlägt die
 * Toolbar-Textfarbe durch — wie bei `CanvasWindowRestoreIcon`.
 *
 * In der Werkzeug-Pille stößt der Pfeil beim Überfahren hinaus (#315, `_toolbarIconMotion.scss`).
 */
export const CanvasApplyTasksIcon: React.FC<CanvasApplyTasksIconProps> = ({
  size = 24, strokeWidth = 2, className,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={['canvas-apply-tasks-icon', className].filter(Boolean).join(' ')}
  >
    {/* Das Dokument — links offen, damit der Pfeil hineinstoßen kann. */}
    <path
      className="canvas-apply-tasks-icon__document"
      d="M14.0002 3.49995V7.49995C14.0002 8.03038 14.2109 8.53909 14.586 8.91416C14.9611 9.28924 15.4698 9.49995 16.0002 9.49995H20.0002M4.0002 18.5L4.0002 19.1C4.0002 19.6304 4.21091 20.1391 4.58598 20.5142C4.96105 20.8892 5.46976 21.0999 6.0002 21.0999H18.0002C18.5306 21.0999 19.0393 20.8892 19.4144 20.5142C19.7895 20.1391 20.0002 19.6304 20.0002 19.1V9.32838C20.0002 8.79794 19.7895 8.28924 19.4144 7.91416L15.586 4.08574C15.2109 3.71067 14.7022 3.49995 14.1718 3.49995H6.0002C5.46976 3.49995 4.96105 3.71066 4.58598 4.08574C4.21091 4.46081 4.0002 4.96952 4.0002 5.49995L4.0002 6.99995"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/*
      Der Pfeil — im Figma eine FLÄCHE, hier als Kontur nachgezeichnet:
      seine Mittellinie (Spitze bei 3,75/12,75, Arme bis 7,75) ist dieselbe,
      aber nur eine Kontur nimmt dieselbe Strichstärke an wie das Dokument
      darüber. Als Fläche blieb er bei 1,5 und wirkte neben Papierkorb und
      Nutzer-Icon (Lucide, Strich 2) dünn.
    */}
    <path
      className="canvas-apply-tasks-icon__arrow"
      d="M12.75 12.75H3.75M7.75 8.75L3.75 12.75L7.75 16.75"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
